import { CareEnvironment, CarePhase } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH, unlimitedMaxBenefitMagicNumber } from '../sharedConstants';
import { computeMonthlyCostMetrics, computeNon2AdlDaysThisRow } from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  compoundFactor,
  getCalendarPolicyYear,
  getCareCostAnchorYear,
  getDailyBenefitAmountForEnvironment,
  getFirstContributingPhaseStartYear,
  getPhaseCareMonthsByPhase,
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
  resolveRowPolicyYear,
} from '../sharedPolicyConfig';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import {
  deriveIneligibilityReason,
  deriveIneligibleDayBreakdown,
} from '../deriveIneligibilityReason';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';
import { applyIndemnitySurplus } from '../sharedSurplus';
import {
  Claimant,
  JointWaitingPeriod,
  partnerWaitingPeriodSeed,
  seedJointWaitingPeriod,
} from '../jointClaimant';
import { deriveAtIssuePools, forwardBenefitPeriodTotal } from './durationPoolModel';

// ---------------------------------------------------------------------------
// OneAmerica Asset Care — hybrid whole life with AOB/COB LTC riders.
//
// Two pools (Acceleration of Benefits drawn first, then Continuation of
// Benefits), per-pool compound inflation at policy anniversary, monthly cap
// inflated by the AOB rate, flat Face Amount death benefit (depleted dollar-
// for-dollar by AOB drawdown). No retroactive WP lump; EP days are out of
// pocket.
//
// Reimbursement OR cash indemnity:
// - Reimbursement: pay min(eligibleCost, proRatedCap), drawdown from AOB
//   then COB.
// - Indemnity (AOB-only, per illustration p.3/p.13): pay
//   monthlyCap × indemnityPercent × (eligibleDays/30) as cash regardless of
//   actual cost, drawdown from AOB. Once AOB is exhausted the strategy
//   falls back to reimbursement on COB — illustration text says cash
//   indemnity is "Acceleration of Benefits rider only", which we read as
//   "indemnity is unavailable on COB". The reading is defensible but the
//   PDF never spells out the COB-fallback rule literally; see
//   docs/oa-asset-care/field-mapping.md. The fall-back is day-granular
//   (P2-9): in the AOB-exhausting month the remaining eligible days revert
//   to COB reimbursement within the same row.
//   The indemnity percent defaults to the product's fixed 75% cap when the
//   field is missing or non-positive (P2-10; field-mapping.md "Always
//   return 0.75 for Asset Care … the cap is a fixed property of the
//   product"). The IRS per-diem indemnity cap remains out of scope.
//
// Like Asset Flex, Asset Care needs no per-claim or per-month carry-forward
// beyond the universal pools/waitingPeriod/facts/deltas — both state and
// deltas slots are empty.
// ---------------------------------------------------------------------------

// Asset Care's cash indemnity is capped at 75% of the monthly benefit limit —
// a fixed property of the product (docs/oa-asset-care/field-mapping.md). Used
// when `policyIndemnityBenefitPercent` is missing or non-positive (P2-10).
export const ASSET_CARE_DEFAULT_INDEMNITY_PERCENT = 0.75;

// Surfaced on `terms.dataWarnings` for partially-migrated rows (mirrors NYL
// Asset Flex P2-13): the benefit-period model is all-or-nothing, so a row with
// exactly one of the two benefit-period fields stays fully on the legacy dollar
// inputs and asks for a re-extraction.
export const OA_ASSET_CARE_MIXED_MODEL_WARNING =
  'This Asset Care policy has only one of the two LTC benefit-period fields (AOB / COB), so the ' +
  'benefit-period pool model cannot be applied consistently. Both pools use the stored dollar ' +
  'amounts instead. Re-upload the illustration (or complete both benefit-period fields) to ' +
  'enable the benefit-period model.';

// Sub-microdollar FP residue left behind by an exhausting draw must not keep a
// pool "open" — a revived pool would earn phantom inflation accruals at later
// anniversaries (the at-issue accrual gates on `pool > 0`). Close-of-month
// balances below the tolerance snap to exactly 0.
const POOL_EXHAUSTION_TOLERANCE = 1e-6;
const snapExhaustedPool = (balance: number): number =>
  balance < POOL_EXHAUSTION_TOLERANCE ? 0 : balance;

// Joint runs carry per-claimant waiting-period balances here. The universal
// MonthlyState.waitingPeriod is single-insured, so for joint we persist both
// spouses' EP balances in strategyState and surface the active claimant's onto
// each row's waitingPeriod slot (keeping per-row identity checks consistent).
// Undefined on single-life — Asset Care needs no carried state there.
export interface OaAssetCareState {
  // Indemnity-surplus reserve. Indemnity policies pay a fixed monthly benefit
  // regardless of cost; the excess above eligible cost is banked here and
  // drained on later shortfall months (legacy `applyIndemnityPayoutToPhase`
  // parity). Always 0 on reimbursement-election policies (their payout never
  // exceeds eligible cost), so the surplus model is a numeric no-op there.
  indemnitySurplus: number;
  jointWaitingPeriod?: JointWaitingPeriod;
}
export interface OaAssetCareDeltas {
  indemnitySurplusAccrued: number;
  indemnitySurplusApplied: number;
  // COB reimbursement paid on an indemnity-election row (the P2-9 day-granular
  // transition month is the only row that mixes AOB cash with COB
  // reimbursement). `payout.indemnityPayout` carries only the AOB cash, so
  // cash-payout rollups (`getMonthPayout`) add this on top — without it the
  // transition row's COB sliver vanishes from cumulative payout whenever care
  // cost sits below the indemnity rate. Always 0 on reimbursement elections.
  cobReimbursementPaid: number;
}

interface InflationConfig {
  baseRate: number;
  eobRate: number;
  capYears: number;
}

// Each pool reads its own inflation rate, falling back to the policy-wide
// inflation rate when the pool-specific knob is missing. The fallback
// matters when a policy carries only a single inflation rate
// (`policyInflationProtection`) and not the per-pool overrides — common
// for older uploads and any product whose illustration lists one rate that
// applies to both pools.
function readInflation(ltcPolicy: PolicyFundingSource): InflationConfig {
  const policyWide = ltcPolicy.policyInflationProtection ?? 0;
  return {
    baseRate: ltcPolicy.policyInflationProtectionOnFaceAmount ?? policyWide,
    eobRate: ltcPolicy.policyInflationProtectionOnCOBAmount ?? policyWide,
    capYears: ltcPolicy.policyInflationProtectionYears ?? 0,
  };
}

// Asset Care offers a Lifetime/Unlimited COB rider, and legacy
// `calcPolicyTotalLtcLimitForPhase` treats a missing COB amount on a hybrid as
// UNLIMITED — not $0 (`isUnlimitedBenefit` → `handleUnlimitedBenefit`). The
// extractor stores that election as a null `policyContinuationBenefitAmount`
// ("unlimited" parses to null) with a null COB benefit period ("no finite
// period"), while a no-COB-rider policy post-processes to a populated 0 and a
// finite COB carries a dollar amount or a benefit-period month count. So both
// fields null ⇒ unlimited. Mirrors hybridLifeIraStrategy / traditionalLtc:
// seed the sentinel, keep it flat (no inflation accrual, no prior-utilization
// dent), and set `terms.isUnlimited`.
function isUnlimitedCob(ltcPolicy: PolicyFundingSource): boolean {
  return (
    ltcPolicy.policyContinuationBenefitAmount == null &&
    ltcPolicy.policyEobBenefitDurationMonths == null
  );
}

// Strategy-local wrap of the shared duration-model sizing: an unlimited COB
// seeds the sentinel pool (the shared helper's `?? 0` would collapse it to a
// hard $0 and payouts would wrongly stop at AOB exhaustion). The AOB side is
// untouched — with no finite COB period the duration model stays off and the
// base pool keeps its legacy Face Amount sizing. An unlimited COB has no
// finite COB period by definition, so a populated AOB period alone is NOT a
// partially-migrated record — the mixed-model warning is suppressed.
function deriveOaAtIssuePools(
  ltcPolicy: PolicyFundingSource,
): ReturnType<typeof deriveAtIssuePools> {
  const pools = deriveAtIssuePools(ltcPolicy);
  if (!isUnlimitedCob(ltcPolicy)) return pools;
  return { ...pools, eobAtIssue: unlimitedMaxBenefitMagicNumber, hasMixedModelFields: false };
}

// Prior utilization drains the AOB (base) pool first; anything beyond it
// spills into the COB pool (mirrors indexedAnnuityCareStrategy's
// applyPriorUtilization) — a policy drawn past its base pool opens with a
// reduced COB, not a clamped-away remainder.
function splitPriorUtilization(
  utilizedToDate: number,
  baseAtClaimStart: number,
  eobAtClaimStart: number,
  eobIsUnlimited: boolean,
): { priorBaseUtilization: number; priorCobUtilization: number } {
  const priorBaseUtilization = Math.min(Math.max(utilizedToDate, 0), baseAtClaimStart);
  // An unlimited COB pool is not dented by prior utilization — legacy applies
  // the sentinel before utilized-to-date enters the calc.
  const priorCobUtilization = eobIsUnlimited
    ? 0
    : Math.min(Math.max(utilizedToDate - priorBaseUtilization, 0), eobAtClaimStart);
  return { priorBaseUtilization, priorCobUtilization };
}

export const oaAssetCareStrategy: PolicyProjectionStrategy<OaAssetCareState, OaAssetCareDeltas> = {
  initClaimState(client, ltcPolicy, jointAnchorPolicyYear): MonthlyState<OaAssetCareState> {
    const { baseAtIssue, eobAtIssue } = deriveOaAtIssuePools(ltcPolicy);
    const cobIsUnlimited = isUnlimitedCob(ltcPolicy);
    // The Face Amount (death benefit) is independent of pool sizing under the
    // benefit-period model: the AOB pool is monthly × accelMonths and inflates,
    // while the face stays flat. AOB drawdown depletes the face proportionally
    // (see computeMonth), so the two need not be equal.
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const { baseRate, eobRate, capYears } = readInflation(ltcPolicy);
    // Anniversary inflation compounds every policy year regardless of
    // whether a claim has started; the projection only spans claim months,
    // so anniversaries between policy issue and claim start would otherwise
    // be silently skipped. Pre-inflate at claim start so opening pools
    // match the carrier's actual current pool. Tests where the claim starts
    // at policy year 1 keep both factors at 1.0. Joint pre-inflates to the
    // shared anchor (earliest claim start across both spouses).
    const claimStartPolicyYear =
      jointAnchorPolicyYear ?? getCalendarPolicyYear(client as Client, 0);
    const baseAtClaimStart = baseAtIssue * compoundFactor(baseRate, capYears, claimStartPolicyYear);
    // An unlimited COB pool stays flat at the sentinel — inflating a magic
    // number is meaningless and would break the >= sentinel "Unlimited"
    // rendering convention.
    const eobAtClaimStart = cobIsUnlimited
      ? eobAtIssue
      : eobAtIssue * compoundFactor(eobRate, capYears, claimStartPolicyYear);
    const { priorBaseUtilization, priorCobUtilization } = splitPriorUtilization(
      utilizedToDate,
      baseAtClaimStart,
      eobAtClaimStart,
      cobIsUnlimited,
    );
    const waitingPeriod: WaitingPeriodState = {
      homeDays:
        ltcPolicy.homeCareWaitingPeriodDaysRemaining ?? ltcPolicy.homeCareWaitingPeriodDays ?? 0,
      facilityDays:
        ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
        ltcPolicy.facilityCareWaitingPeriodDays ??
        0,
    };
    // Joint: each spouse serves their own elimination period. Any
    // `...DaysRemaining` partial credit belongs to the primary client's
    // in-flight claim only; the partner seeds from the full policy EP.
    const isJoint = jointAnchorPolicyYear != null;
    return {
      pools: {
        baseBenefitPool: Math.max(baseAtClaimStart - priorBaseUtilization, 0),
        extensionOfBenefitsPool: Math.max(eobAtClaimStart - priorCobUtilization, 0),
        // The face opens net of utilization, floored — a partially used policy
        // opens reduced (AOB drawdown accelerates the face dollar-for-dollar).
        deathBenefit: Math.max(faceAmount - utilizedToDate, minGuaranteed),
      },
      waitingPeriod,
      strategyState: isJoint
        ? {
            indemnitySurplus: 0,
            jointWaitingPeriod: seedJointWaitingPeriod(
              waitingPeriod,
              partnerWaitingPeriodSeed(ltcPolicy),
            ),
          }
        : { indemnitySurplus: 0 },
    };
  },

  buildClaimTerms(client, ltcPolicy, jointAnchorPolicyYear): ClaimTerms {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const { baseRate, eobRate, capYears } = readInflation(ltcPolicy);
    const clientAsClient = client as Client;
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);
    const { baseAtIssue, eobAtIssue, usesDurationModel, hasMixedModelFields } =
      deriveOaAtIssuePools(ltcPolicy);
    const cobIsUnlimited = isUnlimitedCob(ltcPolicy);
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    // Prior utilization deducted from the AOB (base) pool first, spilling into
    // the COB pool past the base (mirrors `initClaimState`). Both pools are
    // pre-inflated to the SAME anchor `initClaimState` used (the shared joint
    // anchor when the partner claims first) so the caps match the deduction.
    const claimStartPolicyYear =
      jointAnchorPolicyYear ?? getCalendarPolicyYear(clientAsClient, 0);
    const baseAtClaimStart = baseAtIssue * compoundFactor(baseRate, capYears, claimStartPolicyYear);
    const eobAtClaimStart = cobIsUnlimited
      ? eobAtIssue
      : eobAtIssue * compoundFactor(eobRate, capYears, claimStartPolicyYear);
    const { priorBaseUtilization, priorCobUtilization } = splitPriorUtilization(
      ltcPolicy.policyBenefitUtilizedToDate ?? 0,
      baseAtClaimStart,
      eobAtClaimStart,
      cobIsUnlimited,
    );
    const priorUtilization = priorBaseUtilization + priorCobUtilization;
    return {
      strategyId: 'oa-asset-care',
      priorUtilization,
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      // Resolve every environment through the same helper the payout math uses,
      // so the displayed cap matches the math. A raw `?? 0` would collapse a
      // sparse/unpopulated env field to a hard $0 the presentation can't rescue
      // (0 isn't nullish), misrepresenting the cap; the helper honors explicit 0
      // and falls back to the max populated daily for sparse imports.
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.home,
        ),
        [CareEnvironment.assistedLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.assistedLiving,
        ),
        [CareEnvironment.fullCareFacility]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.fullCareFacility,
        ),
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      // Asset Care has no single product-wide ACIO knob; populate
      // inflationRate from the AOB rate for legacy/UI compatibility.
      inflationRate: baseRate,
      inflationCapYears: capYears,
      baseBenefitPoolInflationRate: baseRate,
      baseBenefitPoolInflationCapYears: capYears,
      extensionOfBenefitsPoolInflationRate: eobRate,
      extensionOfBenefitsPoolInflationCapYears: capYears,
      baseBenefitPoolAtIssue: baseAtIssue,
      extensionOfBenefitsPoolAtIssue: eobAtIssue,
      // Surface the AOB/COB benefit periods only when the benefit-period model
      // is active (all-or-nothing — P2-13). Legacy and partially-migrated
      // policies (dollar inputs) leave these undefined so the UI does not render
      // a misleading "× N months" derivation.
      baseBenefitPoolDurationMonths: usesDurationModel
        ? ltcPolicy.policyAccelerationBenefitDurationMonths ?? undefined
        : undefined,
      extensionOfBenefitsPoolDurationMonths: usesDurationModel
        ? ltcPolicy.policyEobBenefitDurationMonths ?? undefined
        : undefined,
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear: getFirstContributingPhaseStartYear(clientAsClient),
      policyPremiumStartYear: ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear,
      minimumGuaranteedDeathBenefit: ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0,
      isReimbursement: !isIndemnity,
      isIndemnity,
      // Unlimited COB makes the LIFETIME pool unlimited (base + sentinel COB
      // never exhausts). UI surfaces gate on this to render "Unlimited"
      // instead of the raw ~$1B running balance; the base pool itself stays
      // finite, so per-pool surfaces must check each pool against the
      // sentinel rather than this flag.
      isUnlimited: cobIsUnlimited,
      ...(hasMixedModelFields ? { dataWarnings: [OA_ASSET_CARE_MIXED_MODEL_WARNING] } : {}),
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear } = input;
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const { baseRate, eobRate, capYears } = readInflation(ltcPolicy);

    const baseFactor = compoundFactor(baseRate, capYears, policyYear);
    const eobFactor = compoundFactor(eobRate, capYears, policyYear);

    const { baseAtIssue, eobAtIssue, usesDurationModel } = deriveOaAtIssuePools(ltcPolicy);
    const inflatedBaseMonthly = baseMonthly * baseFactor;
    // The COB monthly limit grows on the COB rate, independent of AOB — so a
    // 0% AOB / non-zero COB policy still inflates the cap during the COB phase.
    const inflatedEobMonthly = baseMonthly * eobFactor;
    // Pool PRINCIPAL at this policy year (the at-issue pool inflated to here).
    // Under the benefit-period model this equals monthly × benefit-period
    // months × factor — exactly the opening pool a claim starting this year
    // would carry, so the accumulation-timeline AOB/COB columns line up with
    // the in-claim opening balance. Distinct from the forward-summed total below.
    // An unlimited COB stays flat at the sentinel (>= sentinel renders
    // "Unlimited"); only its monthly cap inflates.
    const inflatedAcceleration = baseAtIssue * baseFactor;
    const inflatedEob = isUnlimitedCob(ltcPolicy) ? eobAtIssue : eobAtIssue * eobFactor;
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;

    // Carrier's printed "Total LTC Benefit" for a claim starting this year:
    // the inflating monthly benefit summed across the full AOB+COB benefit
    // period. Exceeds the principal (inflatedAcceleration + inflatedEob) by the
    // inflation that accrues during the claim; the two converge at the
    // inflation cap year. Only under the benefit-period model — legacy
    // dollar-pool policies have no month count to sum over.
    const forwardLtcBenefitTotal = usesDurationModel
      ? forwardBenefitPeriodTotal(ltcPolicy, policyYear, baseMonthly, baseRate, eobRate, capYears)
      : undefined;

    return {
      policyYear,
      effectiveDailyBenefit: inflatedBaseMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: inflatedBaseMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitRider: inflatedEobMonthly / DAYS_IN_MONTH,
      effectiveFaceAmount: faceAmount,
      effectiveContinuationBenefit: inflatedEob,
      effectiveTotalLtcBenefit: inflatedAcceleration + inflatedEob,
      monthlyBenefitCap: inflatedBaseMonthly,
      continuationMonthlyBenefitCap: inflatedEobMonthly,
      theoreticalLtcPoolCeiling: inflatedAcceleration + inflatedEob,
      ...(forwardLtcBenefitTotal !== undefined ? { forwardLtcBenefitTotal } : {}),
    };
  },

  computeMonth(
    input: MonthComputeInput<OaAssetCareState, OaAssetCareDeltas>,
  ): MonthlyEntry<OaAssetCareState, OaAssetCareDeltas> {
    const {
      client,
      ltcPolicy,
      phaseCarePlan,
      monthlyCareInput,
      openState,
      priorMonth,
      priorClaimantMonth,
    } = input;
    const { monthIndex, carePhase, monthsIntoPhase, claimant } = monthlyCareInput;
    const clientAsClient = client as Client;
    // Joint-ness is read from the carried state (per-claimant WP present), not
    // the row tag, so a joint run preserves both spouses' balances regardless.
    const jointWaitingPeriodBefore = openState.strategyState.jointWaitingPeriod;
    const isJoint = jointWaitingPeriodBefore != null;
    const activeClaimant: Claimant = claimant ?? 'client';

    // Cost metrics + structural policy year, resolved up front so the
    // pre-policy gate runs before any WP/ADL classification.
    const costMetrics = computeMonthlyCostMetrics(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const claimYearOffset = Math.floor(monthIndex / 12);
    const policyYear = resolveRowPolicyYear(
      clientAsClient,
      monthIndex,
      input.jointAnchorPolicyYear,
    );

    // Each spouse serves their own elimination period: joint reads the active
    // claimant's balance from strategyState (the universal waitingPeriod slot is
    // rewritten per row and would otherwise carry the prior row's spouse).
    const activeWaitingPeriodBefore: WaitingPeriodState = jointWaitingPeriodBefore
      ? jointWaitingPeriodBefore[activeClaimant]
      : openState.waitingPeriod;
    // The row's openState surfaces the active claimant's WP-before so identity
    // checks + UI read per-claimant. Shared pools pass through unchanged.
    const rowOpenState: MonthlyState<OaAssetCareState> = isJoint
      ? { ...openState, waitingPeriod: activeWaitingPeriodBefore }
      : openState;

    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      claimYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      claimant: monthlyCareInput.claimant,
    };

    // Pre-policy gate: a policy cannot pay for care delivered before its
    // premium-start year (policy year < 1). Book the full cost out-of-pocket
    // and carry every balance forward untouched. Placed before WP/ADL
    // classification and all payout math. (Single-life floors policy year at 1
    // upstream, so only joint partner rows can reach here.)
    if (policyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<OaAssetCareState, OaAssetCareDeltas>({
        context,
        facts: prePolicyFacts,
        openState: rowOpenState,
        strategyDeltas: {
          indemnitySurplusAccrued: 0,
          indemnitySurplusApplied: 0,
          cobReimbursementPaid: 0,
        },
      });
    }

    // 1. Day classification (2-ADL bucket → WP → eligible).
    const non2AdlDays = computeNon2AdlDaysThisRow(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDays;

    // Asset Care EP: 0 days for informal/home/adult-day; 90 days for
    // facility (AL, nursing, international). Routed by care environment;
    // the existing two-tier WP fields cover both buckets.
    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      activeWaitingPeriodBefore,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = wpConsumption;
    const eligibleDays = daysAfter2Adl - wpDaysThisRow;

    // 2. Per-pool monthly dollar caps for this policy year. The AOB cap grows
    //    on the AOB rate, the COB cap on the COB rate — so once AOB is
    //    exhausted and the draw moves to COB, the monthly limit reflects COB
    //    inflation (mirroring the carrier's separate AOB/COB Monthly Benefit
    //    Limit columns) instead of staying pinned to the AOB rate.
    //    `costMetrics` and `policyYear` were resolved up front for the
    //    pre-policy gate.
    const { baseRate, eobRate, capYears } = readInflation(ltcPolicy);

    const baseMonthly = getMonthlyBenefitAmountForEnvironment(
      ltcPolicy,
      phaseCarePlan.appliedCareEnvironment,
    );
    const baseMonthlyCap = baseMonthly * compoundFactor(baseRate, capYears, policyYear);
    const eobMonthlyCap = baseMonthly * compoundFactor(eobRate, capYears, policyYear);

    // 3. Annual anniversary accrual computed against the AT-ISSUE pool amount
    //    (not the remaining balance), but only on a pool that's still open.
    //    This keeps each pool inflating in lock-step with its monthly cap, so a
    //    claim draws ~benefit-period months at the inflating cap (matching the
    //    carrier's forward-summed Total LTC Benefit) instead of inflating a
    //    pre-baked dollar pool. Once a pool drains to $0 it stays exhausted —
    //    inflation cannot reopen a closed pool. The bare `> 0` gates are
    //    reliable because close-of-month balances snap FP residue to exactly 0.
    //    (Mirrors NYL Asset Flex.)
    let baseInflationAccrual = 0;
    let eobInflationAccrual = 0;
    if (priorMonth && policyYear !== priorMonth.context.policyYear) {
      const priorYear = priorMonth.context.policyYear;
      const { baseAtIssue: baseInitial, eobAtIssue: eobInitial } = deriveOaAtIssuePools(ltcPolicy);
      const baseFactorDelta =
        compoundFactor(baseRate, capYears, policyYear) -
        compoundFactor(baseRate, capYears, priorYear);
      const eobFactorDelta =
        compoundFactor(eobRate, capYears, policyYear) -
        compoundFactor(eobRate, capYears, priorYear);
      if (openState.pools.baseBenefitPool > 0) {
        baseInflationAccrual = baseInitial * baseFactorDelta;
      }
      // An unlimited COB pool stays flat at the sentinel — no anniversary
      // accrual (mirrors hybridLifeIra; inflating a magic number is
      // meaningless and only its monthly cap grows).
      if (openState.pools.extensionOfBenefitsPool > 0 && !isUnlimitedCob(ltcPolicy)) {
        eobInflationAccrual = eobInitial * eobFactorDelta;
      }
    }
    const baseAfterAccrual = openState.pools.baseBenefitPool + baseInflationAccrual;
    const eobAfterAccrual = openState.pools.extensionOfBenefitsPool + eobInflationAccrual;

    // 4. Payout: branches on indemnity vs. reimbursement. Indemnity is
    //    AOB-only — once AOB is exhausted we revert to reimbursement on
    //    COB. The revert is DAY-granular (P2-9): in the month where the AOB
    //    exhausts, the indemnity covers only the AOB-funded days and the
    //    remaining eligible days fall through to COB reimbursement below.
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    // Asset Care caps cash indemnity at 75% of the monthly benefit limit —
    // a fixed property of the product (field-mapping.md) — so a missing or
    // non-positive percent defaults to 0.75, not 100% (P2-10). The upload
    // path always writes 0.75; this covers hand-entered and pre-field rows.
    const indemnityPercentRaw = ltcPolicy.policyIndemnityBenefitPercent;
    const indemnityPercent =
      indemnityPercentRaw != null && Number.isFinite(indemnityPercentRaw) && indemnityPercentRaw > 0
        ? indemnityPercentRaw
        : ASSET_CARE_DEFAULT_INDEMNITY_PERCENT;
    const baseProRatedCap = baseMonthlyCap * (eligibleDays / DAYS_IN_MONTH);

    let drawFromBase = 0;
    let drawFromEob = 0;
    let indemnityPaid = 0;
    if (isIndemnity && baseAfterAccrual > 0) {
      // Pay capped indemnity from AOB regardless of actual cost. Drawdown
      // cannot exceed the pool itself (last AOB month may pay less than
      // the capped amount). Indemnity is AOB-only at the AOB cap.
      const fullIndemnity = baseProRatedCap * indemnityPercent;
      const indemnityAmount = Math.min(fullIndemnity, baseAfterAccrual);
      drawFromBase = indemnityAmount;
      indemnityPaid = indemnityAmount;
      if (indemnityAmount < fullIndemnity) {
        // Mid-month AOB exhaustion (P2-9): the pool funded only part of the
        // month's eligible days at the indemnity rate. The remaining eligible
        // days revert to COB reimbursement — the same day-by-day split the
        // reimbursement branch applies, at the COB cap.
        const baseDailyIndemnity = (baseMonthlyCap / DAYS_IN_MONTH) * indemnityPercent;
        const baseDaysFunded =
          baseDailyIndemnity > 0 ? indemnityAmount / baseDailyIndemnity : 0;
        const remainingEligibleDays = Math.max(eligibleDays - baseDaysFunded, 0);
        const eobPerDay = Math.min(costMetrics.dailyCareCost, eobMonthlyCap / DAYS_IN_MONTH);
        const eobDaysFunded =
          eobPerDay > 0 ? Math.min(remainingEligibleDays, eobAfterAccrual / eobPerDay) : 0;
        drawFromEob = eobPerDay * eobDaysFunded;
      }
    } else {
      // Reimbursement, or indemnity-with-AOB-exhausted. Reimburse each
      // eligible day up to that pool's daily cap, AOB first then COB. AOB
      // covers as many eligible days as its pool allows (at the AOB cap); the
      // remaining eligible days fall to COB (at the COB cap). When the two
      // rates are equal this reduces exactly to the prior single-cap math.
      const dailyCost = costMetrics.dailyCareCost;
      const baseDailyCap = baseMonthlyCap / DAYS_IN_MONTH;
      const eobDailyCap = eobMonthlyCap / DAYS_IN_MONTH;

      const basePerDay = Math.min(dailyCost, baseDailyCap);
      const baseDaysFunded =
        basePerDay > 0 ? Math.min(eligibleDays, baseAfterAccrual / basePerDay) : 0;
      drawFromBase = basePerDay * baseDaysFunded;

      const eobPerDay = Math.min(dailyCost, eobDailyCap);
      const eobDaysFunded =
        eobPerDay > 0 ? Math.min(eligibleDays - baseDaysFunded, eobAfterAccrual / eobPerDay) : 0;
      drawFromEob = eobPerDay * eobDaysFunded;
    }
    // Gross cash this row pays out across both pools. On indemnity this is the
    // capped cash regardless of cost; on reimbursement it is the reimbursed
    // amount (≤ eligible cost). The pools deplete by this gross cash; the
    // surplus model is a separate accounting reserve layered on top.
    const rowPayout = drawFromBase + drawFromEob;

    // 4b. Indemnity-surplus accounting (per-row). Cost-coverage is capped at
    //     eligible cost; cash above eligible cost banks as surplus and drains
    //     on later shortfall rows. On reimbursement-election policies the open
    //     surplus is always 0 and the reimbursed payout never exceeds eligible
    //     cost, so this is a numeric no-op (no accrual, no drain).
    const eligibleCost = costMetrics.dailyCareCost * eligibleDays;
    const surplusResult = applyIndemnitySurplus({
      openSurplus: openState.strategyState.indemnitySurplus,
      indemnityPayout: rowPayout,
      eligibleCost,
      fullCost: costMetrics.effectiveCareCost,
      // Two pools (AOB + COB): live until BOTH are spent — when AOB exhausts the
      // row reverts to COB reimbursement, so the reserve must still drain there.
      // 1e-6 epsilon absorbs sub-cent IEEE-754 dust from day-funding draws.
      policyExhausted: baseAfterAccrual + eobAfterAccrual <= 1e-6,
    });

    // 5. Closing balances. Only AOB drawdown depletes the death benefit (COB is
    //    a separate pool and doesn't accelerate the face), and it does so in
    //    direct proportion to the remaining AOB pool — the DB falls by the same
    //    percentage as the pool, not dollar-for-dollar. Under benefit-period
    //    sizing the AOB pool inflates beyond the flat face, so dollar-for-dollar
    //    would over-deplete the face; proportional depletion keeps the inflation
    //    portion of a payout from eating extra death benefit (and reduces to the
    //    old dollar-for-dollar behavior when the AOB pool equals the face at 0%
    //    inflation). Floors at the minimum guaranteed DB. (Mirrors NYL Asset
    //    Flex P2-12.)
    const closeBase = snapExhaustedPool(baseAfterAccrual - drawFromBase);
    const closeEob = snapExhaustedPool(eobAfterAccrual - drawFromEob);
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const unflooredCloseDeathBenefit =
      baseAfterAccrual > 0
        ? openState.pools.deathBenefit * (closeBase / baseAfterAccrual)
        : openState.pools.deathBenefit;
    const closeDeathBenefit = Math.max(unflooredCloseDeathBenefit, minGuaranteed);
    const deathBenefitReduction = openState.pools.deathBenefit - closeDeathBenefit;
    const deathBenefitFloored =
      closeDeathBenefit === minGuaranteed && unflooredCloseDeathBenefit < minGuaranteed;

    // The row's applied cap follows the pool funding it: any AOB draw labels
    // the row 'base' (the P2-9 transition row pays a day-granular AOB+COB mix
    // but still reports the base cap); only rows funded purely from COB — or
    // post-AOB rows where the next payable dollar is COB's — report 'eob'.
    const inCobPhase = drawFromBase <= 0 && closeBase <= 0 && (closeEob > 0 || drawFromEob > 0);

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays,
      coverageEligibleDays: eligibleDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      appliedMonthlyBenefitCap: inCobPhase ? eobMonthlyCap : baseMonthlyCap,
      appliedCapPoolSource: inCobPhase ? 'eob' : 'base',
    };

    const payout: PayoutSummary = {
      ltcCoveragePaid: surplusResult.ltcCoveragePaid,
      ltcPayoutToCost: surplusResult.ltcPayoutToCost,
      ineligibleCost: costMetrics.effectiveCareCost - surplusResult.ltcPayoutToCost,
      indemnityPayout: indemnityPaid,
    };

    const deltas: MonthlyDeltas<OaAssetCareDeltas> = {
      payout,
      base: { depletion: drawFromBase, inflationAccrual: baseInflationAccrual },
      eob: { depletion: drawFromEob, inflationAccrual: eobInflationAccrual },
      deathBenefit: {
        reduction: deathBenefitReduction,
        flooredAtMinimum: deathBenefitFloored,
      },
      strategyDeltas: {
        indemnitySurplusAccrued: surplusResult.surplusAccrued,
        indemnitySurplusApplied: surplusResult.surplusApplied,
        cobReimbursementPaid: isIndemnity ? drawFromEob : 0,
      },
    };

    const activeWaitingPeriodAfter: WaitingPeriodState = {
      homeDays: closeHomeWp,
      facilityDays: closeFacilityWp,
    };
    const closeState: MonthlyState<OaAssetCareState> = {
      pools: {
        baseBenefitPool: closeBase,
        extensionOfBenefitsPool: closeEob,
        deathBenefit: closeDeathBenefit,
      },
      waitingPeriod: activeWaitingPeriodAfter,
      // Carry the other spouse's EP balance forward unchanged; update only the
      // active claimant's. The indemnity surplus is shared across the policy.
      strategyState: jointWaitingPeriodBefore
        ? {
            indemnitySurplus: surplusResult.closeSurplus,
            jointWaitingPeriod: {
              ...jointWaitingPeriodBefore,
              [activeClaimant]: activeWaitingPeriodAfter,
            },
          }
        : { indemnitySurplus: surplusResult.closeSurplus },
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closeBase + closeEob <= 0,
      clientPoolExhausted: closeBase + closeEob <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    // Transition milestones (phase/env change, anniversary) compare against
    // `priorClaimantMonth` — the prior row of THIS spouse — not the global
    // previous row, which on a joint policy is often the other spouse (the
    // merged stream interleaves them when their care overlaps) and would fire a
    // spurious transition on every interleaved row. The pool inflation accrual
    // above keeps using the global `priorMonth` so it fires once per shared
    // policy-year boundary, not once per spouse.
    const milestones = deriveStandardMilestones<OaAssetCareState, OaAssetCareDeltas>({
      context,
      facts,
      openState: rowOpenState,
      closeState,
      deltas,
      priorMonth: priorClaimantMonth,
      retroactiveLumpPaid: 0,
      minimumGuaranteedDeathBenefit: minGuaranteed,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection: baseRate > 0 || eobRate > 0,
    });

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState: rowOpenState,
      closeState,
      payout,
    };
    const ineligibilityReason = deriveIneligibilityReason<OaAssetCareState>(ineligibilityInput);
    const ineligibleDayBreakdown =
      deriveIneligibleDayBreakdown<OaAssetCareState>(ineligibilityInput);

    return {
      context,
      facts,
      openState: rowOpenState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
    };
  },

  finalizeDeathBenefit({ closingState }: FinalizeDeathBenefitInput<OaAssetCareState>): number {
    return closingState.pools.deathBenefit;
  },
};
